import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import Sidebar from '../admin-dashboard/components/Sidebar';
import AdminBottomNav from '../admin-dashboard/components/AdminBottomNav';
import AdminMobileHeader from '../admin-dashboard/components/AdminMobileHeader';

const showToast = (msg, type = 'error') => {
  const el = document.createElement('div');
  el.textContent = msg;
  el.style.cssText = `
    position:fixed;bottom:24px;right:24px;z-index:9999;
    padding:12px 20px;border-radius:10px;font-size:13px;font-family:Inter,sans-serif;
    background:${type === 'error' ? '#1a0000' : '#001a00'};
    color:${type === 'error' ? '#ff4444' : '#22c55e'};
    border:1px solid ${type === 'error' ? 'rgba(229,32,42,0.3)' : 'rgba(34,197,94,0.3)'};
    box-shadow:0 4px 20px rgba(0,0,0,0.5);
  `;
  document.body?.appendChild(el);
  setTimeout(() => el?.remove(), 4000);
};

const fmtNum = (n) => {
  if (!n && n !== 0) return '0';
  return Math.round(n)?.toLocaleString('fr-FR')?.replace(/\u202f/g, '\u00a0');
};

const fmtUSD = (n) => {
  if (!n && n !== 0) return '$0';
  return '$' + Math.round(n)?.toLocaleString('fr-FR');
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso)?.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const getPctColor = (pct) => {
  if (pct >= 100) return '#22c55e';
  if (pct >= 70) return '#f97316';
  return '#ef4444';
};

const SkeletonBox = ({ className = '' }) => (
  <div className={`rounded animate-pulse ${className}`} style={{ backgroundColor: '#1f1f1f' }} />
);

const SectionHeader = ({ color, label }) => (
  <div className="flex items-center gap-2 mb-4">
    <div className="w-1 h-4 rounded-full" style={{ backgroundColor: color }} />
    <h2 className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Sora, sans-serif' }}>
      {label}
    </h2>
  </div>
);

const ManagerProfileDetail = () => {
  const { t } = useTranslation();
  const { id: managerId } = useParams();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  const [manager, setManager] = useState(null);
  const [currentData, setCurrentData] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [monthlyHistory, setMonthlyHistory] = useState([]);
  const [servers, setServers] = useState([]);
  const [groupCount, setGroupCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filters for creators table
  const [filterServer, setFilterServer] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Pagination for creators table
  const CREATORS_PAGE_SIZE = 20;
  const [creatorsPage, setCreatorsPage] = useState(1);

  useEffect(() => {
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      if (session?.user?.email) setUserEmail(session?.user?.email);
    });
  }, []);

  const fetchAll = useCallback(async () => {
    if (!managerId) return;
    setLoading(true);
    try {
      // Fetch manager info
      const { data: managerData, error: managerErr } = await supabase
        ?.from('users')
        ?.select('id, name, email, phone, role, active, managed_servers, is_paid, created_at')
        ?.eq('id', managerId)
        ?.single();
      if (managerErr) showToast('Erreur chargement manager');
      else setManager(managerData);

      // Fetch current month data
      const { data: currentRes } = await supabase
        ?.from('creator_current_month')
        ?.select('*, cashback_amount, cashback_pct')
        ?.eq('manager_id', managerId);
      const currentRows = currentRes || [];
      setCurrentData(currentRows);

      // Dynamic CURRENT_PERIOD
      const CURRENT_PERIOD = currentRows?.[0]?.period ||
        `${new Date()?.getFullYear()}-${String(new Date()?.getMonth() + 1)?.padStart(2, '0')}`;

      // Fetch objectives, monthly history, servers, group count in parallel
      const [objRes, histRes, srvRes, grpRes] = await Promise.all([
        supabase?.from('manager_objectives')
          ?.select('server, objective_diamonds, diamonds_m1, objective_recruitment')
          ?.eq('manager_id', managerId)
          ?.eq('period', CURRENT_PERIOD),
        supabase?.from('creator_monthly_history')
          ?.select('period, server, diamonds, total_bonus, charge, net_agency, cashback')
          ?.eq('manager_id', managerId)
          ?.order('period', { ascending: false }),
        supabase?.from('servers')?.select('id, name, color')?.eq('is_active', true),
        supabase?.from('manager_assignments')
          ?.select('id', { count: 'exact', head: true })
          ?.eq('manager_id', managerId),
      ]);

      setObjectives(objRes?.data || []);
      setMonthlyHistory(histRes?.data || []);
      setServers(srvRes?.data || []);
      setGroupCount(grpRes?.count || 0);
    } catch (err) {
      showToast('Erreur chargement données');
    } finally {
      setLoading(false);
    }
  }, [managerId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Server color helper
  const getServerColor = useCallback((serverId) => {
    return servers?.find(s => s?.id === serverId)?.color || '#6b7280';
  }, [servers]);

  // KPI calculations
  const kpis = useMemo(() => {
    const total_diamonds = currentData?.reduce((s, r) => s + (r?.diamonds || 0), 0);
    const active_count = currentData?.filter(r => r?.is_active === true)?.length;
    const total_count = currentData?.length;
    const projection = currentData?.reduce((s, r) => s + (r?.projected_diamonds || 0), 0);
    const bonus_estim = currentData?.reduce((s, r) => s + (r?.bonus_rankup || 0) + (r?.bonus_activeness || 0), 0);
    const new_recruits = currentData?.filter(r => r?.is_new_recruit === true)?.length || 0;
    const total_cashback = currentData?.reduce((sum, r) => sum + (parseFloat(r?.cashback_amount) || 0), 0) || 0;
    return { total_diamonds, active_count, total_count, projection, bonus_estim, new_recruits, total_cashback };
  }, [currentData]);

  // Objectives per server
  const objMap = useMemo(() => {
    const map = {};
    objectives?.forEach(o => { map[o?.server] = o; });
    return map;
  }, [objectives]);

  const serverRealized = useMemo(() => {
    const map = {};
    currentData?.forEach(r => {
      if (!map?.[r?.server]) map[r.server] = 0;
      map[r.server] += r?.diamonds || 0;
    });
    return map;
  }, [currentData]);

  // TikTok objectives
  const tikTokObjectives = useMemo(() => {
    const levelCounts = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    currentData?.forEach(r => {
      const lvl = Math.min(r?.activeness_level || 0, 5);
      levelCounts[lvl] = (levelCounts?.[lvl] || 0) + 1;
    });
    const bonusActiveness = currentData?.reduce((s, r) => s + (r?.bonus_activeness || 0), 0);
    const montee = currentData?.filter(r => r?.tier_status === "Montée d'échelon")?.length;
    const maintien = currentData?.filter(r => r?.tier_status === "Maintien d'échelon" && (r?.tier_current || 0) > 2)?.length;
    const bonusRankup = currentData?.reduce((s, r) => s + (r?.bonus_rankup || 0), 0);
    const totalBonus = bonusActiveness + bonusRankup;
    const projection = kpis?.projection > 0 && kpis?.total_diamonds > 0
      ? (totalBonus / kpis?.total_diamonds) * kpis?.projection
      : 0;
    return { levelCounts, bonusActiveness, montee, maintien, bonusRankup, totalBonus, projection };
  }, [currentData, kpis]);

  // Monthly history aggregated by period
  const historyByPeriod = useMemo(() => {
    const map = {};
    monthlyHistory?.forEach(r => {
      if (!map?.[r?.period]) {
        map[r.period] = { period: r?.period, servers: new Set(), diamonds: 0, total_bonus: 0, charge: 0, net_agency: 0, cashback: 0 };
      }
      map[r.period].diamonds += r?.diamonds || 0;
      map[r.period].total_bonus += r?.total_bonus || 0;
      map[r.period].charge += r?.charge || 0;
      map[r.period].net_agency += r?.net_agency || 0;
      map[r.period].cashback += parseFloat(r?.cashback) || 0;
      if (r?.server) map?.[r?.period]?.servers?.add(r?.server);
    });
    return Object.values(map)
      ?.map(r => ({ ...r, servers: [...r?.servers] }))
      ?.sort((a, b) => b?.period?.localeCompare(a?.period));
  }, [monthlyHistory]);

  // Filtered creators
  const filteredCreators = useMemo(() => {
    return currentData?.filter(r => {
      if (filterServer && r?.server !== filterServer) return false;
      if (filterStatus === 'active' && !r?.is_active) return false;
      if (filterStatus === 'inactive' && r?.is_active) return false;
      return true;
    });
  }, [currentData, filterServer, filterStatus]);

  const paginatedCreators = useMemo(() => {
    return filteredCreators?.slice(
      (creatorsPage - 1) * CREATORS_PAGE_SIZE,
      creatorsPage * CREATORS_PAGE_SIZE
    );
  }, [filteredCreators, creatorsPage]);

  const totalCreatorsPages = Math.ceil((filteredCreators?.length || 0) / CREATORS_PAGE_SIZE);

  useEffect(() => {
    setCreatorsPage(1);
  }, [filterServer, filterStatus]);

  const managedServers = manager?.managed_servers || [];
  const LEVEL_LABELS = ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'];
  const LEVEL_COLORS = ['#6b7280', '#22c55e', '#16a34a', '#15803d', '#166534', '#14532d'];

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: 'var(--color-background)', fontFamily: 'Inter, sans-serif' }}>
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Sora:wght@400;500;600;700;800&display=swap"
        rel="stylesheet"
      />
      <div className="hidden md:flex">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(v => !v)}
          userEmail={userEmail}
        />
      </div>
      <main className="flex-1 overflow-y-auto min-w-0 pb-16 md:pb-0">
        <AdminMobileHeader title={t('admin.profile.profileSubtitle', 'Profil Manager')} />
        {/* Header */}
        <div className="sticky top-0 z-10 px-6 py-4 border-b" style={{ backgroundColor: 'var(--color-background)', borderColor: '#1f1f1f' }}>
          <button
            onClick={() => navigate('/admin/manager-list')}
            className="flex items-center gap-2 text-xs mb-3 transition-colors hover:text-white"
            style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M9 3L5 7l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {t('admin.profile.backToManagers', 'Retour aux managers')}
          </button>
          <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
            {loading ? '...' : (manager?.name || 'Manager')}
          </h1>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>
            {t('admin.profile.profileSubtitle', 'Profil manager — NoiRock')}
          </p>
        </div>

        <div className="p-6 space-y-8">

          {/* SECTION 1 — IDENTITÉ */}
          <section>
            <SectionHeader color="#E5202A" label={t('admin.profile.identity', 'Identité')} />
            {loading ? (
              <div className="rounded-xl p-5 border" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
                <SkeletonBox className="h-12 w-12 rounded-full mb-4" />
                <SkeletonBox className="h-5 w-48 mb-2" />
                <SkeletonBox className="h-3 w-32" />
              </div>
            ) : (
              <div className="rounded-xl p-5 border" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
                <div className="flex items-start gap-4 flex-wrap">
                  {/* Avatar */}
                  <div
                    className="flex-shrink-0 rounded-full flex items-center justify-center text-white font-bold text-xl"
                    style={{ width: '52px', height: '52px', backgroundColor: 'var(--color-primary)', fontFamily: 'Sora, sans-serif' }}
                  >
                    {manager?.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-bold text-white mb-0.5" style={{ fontFamily: 'Sora, sans-serif' }}>
                      {manager?.name || '—'}
                    </h2>
                    <p className="text-sm mb-3" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>
                      {manager?.email || '—'}
                    </p>
                    {/* Badges */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {/* Role badge */}
                      <span className="px-2 py-0.5 rounded text-xs font-bold uppercase" style={{ backgroundColor: 'rgba(229,32,42,0.12)', color: '#E5202A', border: '1px solid rgba(229,32,42,0.25)', fontFamily: 'Sora, sans-serif' }}>
                        {manager?.role || 'manager'}
                      </span>
                      {/* Active badge */}
                      <span className="px-2 py-0.5 rounded text-xs font-bold" style={{
                        backgroundColor: manager?.active !== false ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                        color: manager?.active !== false ? '#22c55e' : '#ef4444',
                        border: `1px solid ${manager?.active !== false ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}`,
                        fontFamily: 'Sora, sans-serif',
                      }}>
                        {manager?.active !== false ? 'Actif' : 'Inactif'}
                      </span>
                      {/* Server badges */}
                      {managedServers?.map(srvId => {
                        const color = getServerColor(srvId);
                        return (
                          <span key={srvId} className="px-2 py-0.5 rounded text-xs font-bold" style={{ backgroundColor: `${color}18`, color, border: `1px solid ${color}30`, fontFamily: 'Sora, sans-serif' }}>
                            {srvId}
                          </span>
                        );
                      })}
                    </div>
                    {/* Details grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {[
                        { label: t('admin.profile.phone', 'Téléphone'), value: manager?.phone || '—' },
                        { label: t('admin.profile.createdAt', 'Créé le'), value: fmtDate(manager?.created_at) },
                        { label: t('admin.profile.groupCount', 'Nb groupes'), value: groupCount },
                        { label: t('admin.profile.creatorCount', 'Nb créateurs'), value: currentData?.length },
                        { label: t('admin.profile.paid', 'Rémunéré'), value: manager?.is_paid ? t('admin.profile.yes', 'Oui') : t('admin.profile.no', 'Non') },
                      ]?.map((item) => (
                        <div key={item?.label} className="rounded-lg p-3" style={{ backgroundColor: '#0d0d0d', border: '1px solid #1f1f1f' }}>
                          <p className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>{item?.label}</p>
                          <p className="text-sm font-semibold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>{item?.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* SECTION 2 — KPI MOIS EN COURS */}
          <section>
            <SectionHeader color="#f97316" label={t('admin.profile.kpiCurrentMonth', 'KPI Mois en cours')} />
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {[
                { label: t('admin.profile.totalDiamonds', 'Total Diamonds'), value: `💎 ${fmtNum(kpis?.total_diamonds)}`, color: '#f97316' },
                { label: t('admin.profile.activeCreators', 'Créateurs actifs'), value: `${kpis?.active_count} / ${kpis?.total_count}`, color: '#22c55e' },
                { label: t('admin.profile.projection', 'Projection'), value: `💎 ${fmtNum(kpis?.projection)}`, color: '#3b82f6' },
                { label: t('admin.profile.estimatedBonus', 'Bonus estimé'), value: fmtUSD(kpis?.bonus_estim), color: '#a855f7' },
                { label: t('admin.profile.newRecruits', 'Nouveaux recrutés'), value: kpis?.new_recruits, color: '#22c55e', sub: t('admin.profile.contractsThisMonth', 'contrats ce mois') },
              ]?.map((card, i) => (
                <div key={i} className="rounded-xl p-3 border" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
                  <p className="text-xs uppercase tracking-wider mb-1.5" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>{card?.label}</p>
                  {loading ? <div className="h-5 w-20 rounded animate-pulse" style={{ backgroundColor: '#1f1f1f' }} /> : (
                    <p className="text-lg font-bold" style={{ color: card?.color, fontFamily: 'Sora, sans-serif' }}>{card?.value}</p>
                  )}
                  {card?.sub && <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.25)', fontFamily: 'Inter, sans-serif' }}>{card?.sub}</p>}
                </div>
              ))}
            </div>
            {/* Cashback KPI card */}
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-xl p-3 border" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
                <p className="text-xs uppercase tracking-wider mb-1.5" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>{t('admin.profile.estimatedCashback', 'Cashback estimé')}</p>
                {loading ? <div className="h-5 w-20 rounded animate-pulse" style={{ backgroundColor: '#1f1f1f' }} /> : (
                  <p className="text-lg font-bold" style={{ color: '#22c55e', fontFamily: 'Sora, sans-serif' }}>${kpis?.total_cashback?.toFixed(2)}</p>
                )}
                <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.25)', fontFamily: 'Inter, sans-serif' }}>total créateurs ce mois</p>
              </div>
            </div>
          </section>

          {/* SECTION 3 — OBJECTIFS PAR SERVEUR */}
          {managedServers?.length > 0 && (
            <section>
              <SectionHeader color="#3b82f6" label={t('admin.profile.objectivesByServer', 'Objectifs par Serveur')} />
              <div className="flex flex-wrap gap-4">
                {managedServers?.map(srvId => {
                  const obj = objMap?.[srvId];
                  const realized = serverRealized?.[srvId] || 0;
                  const objective = obj?.objective_diamonds || 0;
                  const pct = objective > 0 ? (realized / objective) * 100 : 0;
                  const pctColor = getPctColor(pct);
                  const color = getServerColor(srvId);
                  return (
                    <div key={srvId} className="rounded-xl p-4 border flex-1 min-w-[200px]" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-bold px-2 py-0.5 rounded-md" style={{ backgroundColor: `${color}22`, color, fontFamily: 'Sora, sans-serif' }}>
                          {srvId}
                        </span>
                        <span className="text-xs font-bold" style={{ color: pctColor, fontFamily: 'Sora, sans-serif' }}>
                          {objective > 0 ? pct?.toFixed(1) + '%' : '—'}
                        </span>
                      </div>
                      <p className="text-sm font-bold mb-2" style={{ color: 'rgba(255,255,255,0.85)', fontFamily: 'Sora, sans-serif' }}>
                        💎 {fmtNum(realized)} / {fmtNum(objective)}
                      </p>
                      <div className="w-full h-2 rounded-full overflow-hidden mb-2" style={{ backgroundColor: '#1f1f1f' }}>
                        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: pctColor }} />
                      </div>
                      {obj?.diamonds_m1 != null && (
                        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>
                          M-1: 💎 {fmtNum(obj?.diamonds_m1)}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* SECTION 4 — OBJECTIFS TIKTOK À DATE */}
          <section>
            <SectionHeader color="#22c55e" label={t('admin.profile.tiktokObjectives', 'Objectifs TikTok à date')} />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Activeness */}
              <div className="rounded-xl p-4 border" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
                <h3 className="text-xs font-bold uppercase tracking-wider mb-3 text-white" style={{ fontFamily: 'Sora, sans-serif' }}>Activeness</h3>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {LEVEL_LABELS?.map((label, idx) => (
                    <div key={label} className="flex items-center gap-1 px-2 py-1 rounded-lg" style={{ backgroundColor: `${LEVEL_COLORS?.[idx]}18`, border: `1px solid ${LEVEL_COLORS?.[idx]}40` }}>
                      <span className="text-xs font-bold" style={{ color: LEVEL_COLORS?.[idx], fontFamily: 'Sora, sans-serif' }}>{label}</span>
                      <span className="text-xs font-semibold text-white" style={{ fontFamily: 'Inter, sans-serif' }}>{tikTokObjectives?.levelCounts?.[idx] || 0}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>Bonus activeness</p>
                <p className="text-base font-bold" style={{ color: '#22c55e', fontFamily: 'Sora, sans-serif' }}>{fmtUSD(tikTokObjectives?.bonusActiveness)}</p>
              </div>

              {/* Rank-up */}
              <div className="rounded-xl p-4 border" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
                <h3 className="text-xs font-bold uppercase tracking-wider mb-3 text-white" style={{ fontFamily: 'Sora, sans-serif' }}>Rank-Up</h3>
                <div className="space-y-2 mb-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: '#22c55e', fontFamily: 'Inter, sans-serif' }}>↑ Montée d'échelon</span>
                    <span className="text-sm font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>{tikTokObjectives?.montee}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: '#3b82f6', fontFamily: 'Inter, sans-serif' }}>→ Maintien (T3+)</span>
                    <span className="text-sm font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>{tikTokObjectives?.maintien}</span>
                  </div>
                </div>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>Bonus rank-up</p>
                <p className="text-base font-bold" style={{ color: '#f97316', fontFamily: 'Sora, sans-serif' }}>{fmtUSD(tikTokObjectives?.bonusRankup)}</p>
              </div>

              {/* Bonus Total */}
              <div className="rounded-xl p-4 border" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
                <h3 className="text-xs font-bold uppercase tracking-wider mb-3 text-white" style={{ fontFamily: 'Sora, sans-serif' }}>Bonus Total</h3>
                <div className="space-y-2 mb-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>Activeness</span>
                    <span className="text-xs font-semibold" style={{ color: '#22c55e', fontFamily: 'Inter, sans-serif' }}>{fmtUSD(tikTokObjectives?.bonusActiveness)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>Rank-up</span>
                    <span className="text-xs font-semibold" style={{ color: '#f97316', fontFamily: 'Inter, sans-serif' }}>{fmtUSD(tikTokObjectives?.bonusRankup)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>Incrémental</span>
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>$0</span>
                  </div>
                </div>
                <div className="border-t pt-2" style={{ borderColor: '#1f1f1f' }}>
                  <p className="text-xs mb-0.5" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>Total</p>
                  <p className="text-lg font-bold" style={{ color: '#a855f7', fontFamily: 'Sora, sans-serif' }}>{fmtUSD(tikTokObjectives?.totalBonus)}</p>
                  <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>
                    Projection: {fmtUSD(tikTokObjectives?.projection)}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* SECTION 5 — HISTORIQUE MENSUEL */}
          <section>
            <SectionHeader color="#a855f7" label={t('admin.profile.monthlyHistory', 'Historique Mensuel')} />
            <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" style={{ minWidth: '500px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #1f1f1f' }}>
                      {[t('admin.profile.colPeriod', 'Période'), t('admin.profile.colServers', 'Serveurs'), t('admin.profile.colDiamonds', 'Diamonds'), t('admin.profile.colTotalBonus', 'Total Bonus'), t('admin.profile.colCashback', 'Cashback'), t('admin.profile.colCharge', 'Charge'), t('admin.profile.colNetAgency', 'Net Agence')]?.map(h => (
                        <th key={h} className="px-4 py-3 text-left font-semibold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Sora, sans-serif' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      [...Array(4)]?.map((_, i) => (
                        <tr key={i}>
                          {[...Array(7)]?.map((_, j) => (
                            <td key={j} className="px-4 py-3"><SkeletonBox className="h-3 w-16" /></td>
                          ))}
                        </tr>
                      ))
                    ) : historyByPeriod?.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center" style={{ color: 'rgba(255,255,255,0.3)' }}>{t('admin.profile.noHistory', 'Aucun historique')}</td>
                      </tr>
                    ) : (
                      historyByPeriod?.map((row, i) => (
                        <tr key={i} className="border-t" style={{ borderColor: '#1f1f1f' }}
                          onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#1a1a1a'; }}
                          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                          <td className="px-4 py-3 font-medium" style={{ color: 'rgba(255,255,255,0.7)' }}>{row?.period}</td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1 flex-wrap">
                              {row?.servers?.map(srvId => {
                                const color = getServerColor(srvId);
                                return (
                                  <span key={srvId} className="px-1.5 py-0.5 rounded text-xs font-bold" style={{ backgroundColor: `${color}18`, color, fontFamily: 'Sora, sans-serif' }}>
                                    {srvId}
                                  </span>
                                );
                              })}
                            </div>
                          </td>
                          <td className="px-4 py-3 font-semibold text-white">💎 {fmtNum(row?.diamonds)}</td>
                          <td className="px-4 py-3" style={{ color: '#22c55e' }}>{fmtUSD(row?.total_bonus)}</td>
                          <td className="px-4 py-3">
                            {row?.cashback > 0
                              ? <span style={{ color: '#22c55e', fontWeight: '600' }}>${row?.cashback?.toFixed(2)}</span>
                              : <span style={{ color: 'rgba(255,255,255,0.25)' }}>—</span>
                            }
                          </td>
                          <td className="px-4 py-3" style={{ color: '#ef4444' }}>{fmtUSD(row?.charge)}</td>
                          <td className="px-4 py-3" style={{ color: row?.net_agency > 0 ? '#22c55e' : 'rgba(255,255,255,0.5)' }}>{fmtUSD(row?.net_agency)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* SECTION 6 — CRÉATEURS */}
          <section>
            <SectionHeader color="#f59e0b" label={t('admin.profile.creators', 'Créateurs')} />
            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-4">
              <select
                value={filterServer}
                onChange={e => setFilterServer(e?.target?.value)}
                className="px-3 py-1.5 rounded-lg text-base text-white outline-none"
                style={{ backgroundColor: '#111111', border: '1px solid #1f1f1f', fontFamily: 'Inter, sans-serif' }}
              >
                <option value="">{t('admin.profile.allServers', 'Tous les serveurs')}</option>
                {servers?.map(s => <option key={s?.id} value={s?.id}>{s?.name}</option>)}
              </select>
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e?.target?.value)}
                className="px-3 py-1.5 rounded-lg text-base text-white outline-none"
                style={{ backgroundColor: '#111111', border: '1px solid #1f1f1f', fontFamily: 'Inter, sans-serif' }}
              >
                <option value="">{t('admin.profile.allStatuses', 'Tous les statuts')}</option>
                <option value="active">{t('admin.profile.statusActive', 'Actifs')}</option>
                <option value="inactive">{t('admin.profile.statusInactive', 'Inactifs')}</option>
              </select>
              <span className="text-xs self-center" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>
                {filteredCreators?.length} créateur{filteredCreators?.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" style={{ minWidth: '500px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #1f1f1f' }}>
                      {[t('admin.profile.colUsername', 'Username'), t('admin.profile.colServer', 'Serveur'), t('admin.profile.colDiamonds', 'Diamonds'), t('admin.profile.colTier', 'Tier'), t('admin.profile.colActiveness', 'Activeness'), t('admin.profile.colStatus', 'Statut'), t('admin.profile.colBonusRankup', 'Bonus Rankup')]?.map(h => (
                        <th key={h} className="px-4 py-3 text-left font-semibold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Sora, sans-serif' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      [...Array(5)]?.map((_, i) => (
                        <tr key={i}>
                          {[...Array(7)]?.map((_, j) => (
                            <td key={j} className="px-4 py-3"><SkeletonBox className="h-3 w-16" /></td>
                          ))}
                        </tr>
                      ))
                    ) : filteredCreators?.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center" style={{ color: 'rgba(255,255,255,0.3)' }}>{t('admin.profile.noCreator', 'Aucun créateur')}</td>
                      </tr>
                    ) : (
                      filteredCreators?.map((row, i) => {
                        const srvColor = getServerColor(row?.server);
                        const tierStatusStyle = row?.tier_status === "Montée d'échelon"
                          ? { color: '#22c55e', bg: 'rgba(34,197,94,0.1)' }
                          : row?.tier_status === "Maintien d'échelon"
                          ? { color: '#3b82f6', bg: 'rgba(59,130,246,0.1)' }
                          : { color: 'rgba(255,255,255,0.4)', bg: 'rgba(255,255,255,0.06)' };
                        return (
                          <tr
                            key={row?.tiktok_id || i}
                            className="border-t cursor-pointer"
                            style={{ borderColor: '#1f1f1f' }}
                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#1a1a1a'; }}
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                            onClick={() => navigate(`/admin/creators/${row?.tiktok_id}`)}
                          >
                            <td className="px-4 py-3 font-medium text-white">
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span>{row?.username || '—'}</span>
                                {row?.is_new_recruit === true && (
                                  <span style={{
                                    fontSize: '9px', fontWeight: '700',
                                    padding: '1px 5px', borderRadius: '3px',
                                    backgroundColor: 'rgba(34,197,94,0.15)',
                                    color: '#22c55e', marginLeft: '4px',
                                  }}>NEW</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-0.5 rounded text-xs font-bold" style={{ backgroundColor: `${srvColor}18`, color: srvColor, fontFamily: 'Sora, sans-serif' }}>
                                {row?.server || '—'}
                              </span>
                            </td>
                            <td className="px-4 py-3 font-semibold" style={{ color: srvColor }}>💎 {fmtNum(row?.diamonds)}</td>
                            <td className="px-4 py-3">
                              {row?.tier_current != null ? (
                                <span className="px-2 py-0.5 rounded text-xs font-bold" style={{ backgroundColor: 'rgba(107,114,128,0.15)', color: '#9ca3af', border: '1px solid rgba(107,114,128,0.3)' }}>
                                  T{row?.tier_current}
                                </span>
                              ) : '—'}
                            </td>
                            <td className="px-4 py-3">
                              {row?.activeness_level != null ? (
                                <span className="px-2 py-0.5 rounded text-xs font-bold" style={{ backgroundColor: 'rgba(34,197,94,0.1)', color: '#22c55e' }}>
                                  L{row?.activeness_level}
                                </span>
                              ) : '—'}
                            </td>
                            <td className="px-4 py-3">
                              {row?.tier_status ? (
                                <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: tierStatusStyle?.bg, color: tierStatusStyle?.color }}>
                                  {row?.tier_status}
                                </span>
                              ) : '—'}
                            </td>
                            <td className="px-4 py-3" style={{ color: row?.bonus_rankup > 0 ? '#22c55e' : 'rgba(255,255,255,0.3)' }}>
                              {row?.bonus_rankup > 0 ? fmtUSD(row?.bonus_rankup) : '—'}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              {/* Pagination controls */}
              {!loading && filteredCreators?.length > 0 && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 16px', borderTop: '1px solid #1f1f1f'
                }}>
                  <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>
                    {filteredCreators?.length} créateurs · Page {creatorsPage}/{totalCreatorsPages || 1}
                  </span>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      onClick={() => setCreatorsPage(p => Math.max(1, p - 1))}
                      disabled={creatorsPage === 1}
                      style={{
                        padding: '4px 12px', borderRadius: '6px', fontSize: '12px',
                        backgroundColor: 'transparent',
                        color: creatorsPage === 1
                          ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.6)',
                        border: '1px solid #2a2a2a', cursor: creatorsPage === 1 ? 'default' : 'pointer',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >← Préc</button>
                    <button
                      onClick={() => setCreatorsPage(p => Math.min(totalCreatorsPages, p + 1))}
                      disabled={creatorsPage === totalCreatorsPages || totalCreatorsPages === 0}
                      style={{
                        padding: '4px 12px', borderRadius: '6px', fontSize: '12px',
                        backgroundColor: 'transparent',
                        color: creatorsPage === totalCreatorsPages || totalCreatorsPages === 0
                          ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.6)',
                        border: '1px solid #2a2a2a', cursor: creatorsPage === totalCreatorsPages ? 'default' : 'pointer',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >Suiv →</button>
                  </div>
                </div>
              )}
            </div>
          </section>

        </div>
      </main>
      <AdminBottomNav />
    </div>
  );
};

export default ManagerProfileDetail;
